/**
 * Context utilities for browser client
 * Collects page context for event tracking
 */

export interface PageContext {
  /** Full page URL */
  page_url: string;
  /** Page title */
  page_title?: string;
  /** Referrer URL */
  referrer?: string;
  /** Screen width */
  screen_width?: number;
  /** Screen height */
  screen_height?: number;
  /** Viewport width */
  viewport_width?: number;
  /** Viewport height */
  viewport_height?: number;
  /** Device type (desktop/mobile/tablet) */
  site_type?: 'desktop' | 'mobile' | 'tablet';
  /** User's timezone */
  timezone?: string;
  /** User's locale */
  locale?: string;
}

/**
 * Detect device type based on screen width and user agent
 */
export function detectDeviceType(): 'desktop' | 'mobile' | 'tablet' {
  if (typeof window === 'undefined') return 'desktop';

  const ua = navigator.userAgent.toLowerCase();
  const width = window.innerWidth;

  // Check for mobile user agents first
  if (/mobile|android|iphone|ipod|blackberry|opera mini|iemobile/i.test(ua)) {
    return 'mobile';
  }

  // Check for tablet user agents
  if (/ipad|tablet|playbook|silk/i.test(ua)) {
    return 'tablet';
  }

  // Fall back to screen width
  if (width < 768) {
    return 'mobile';
  } else if (width < 1024) {
    return 'tablet';
  }

  return 'desktop';
}

/**
 * Get the user's timezone
 */
export function getTimezone(): string | undefined {
  try {
    return Intl.DateTimeFormat().resolvedOptions().timeZone;
  } catch {
    return undefined;
  }
}

/**
 * Get the user's locale
 */
export function getLocale(): string | undefined {
  if (typeof navigator === 'undefined') return undefined;
  return navigator.language || (navigator as Navigator & { userLanguage?: string }).userLanguage;
}

/**
 * Collect current page context
 */
export function collectPageContext(): PageContext {
  if (typeof window === 'undefined' || typeof document === 'undefined') {
    return { page_url: '' };
  }

  const context: PageContext = {
    page_url: window.location.href,
  };

  // Page title
  if (document.title) {
    context.page_title = document.title;
  }

  // Referrer
  if (document.referrer) {
    context.referrer = document.referrer;
  }

  // Screen dimensions
  if (window.screen) {
    context.screen_width = window.screen.width;
    context.screen_height = window.screen.height;
  }

  // Viewport dimensions
  context.viewport_width = window.innerWidth;
  context.viewport_height = window.innerHeight;

  // Device type
  context.site_type = detectDeviceType();

  // Timezone
  context.timezone = getTimezone();

  // Locale
  context.locale = getLocale();

  return context;
}

/**
 * Get user agent string
 */
export function getUserAgent(): string | undefined {
  if (typeof navigator === 'undefined') return undefined;
  return navigator.userAgent;
}
